import React from 'react'
import moment from 'moment'
import classNames from 'classnames'
import styled from '@emotion/styled'
import { NegotiationStatus, NegotiationDetail, NegotiationType } from 'api'
import { CountdownTimer } from 'view/components/timers'
import { Tooltip } from 'view/components/tooltip'
import { isQuotable, isTerminalStatus, getIsValidOTWDuration } from 'view/routes/requests/shared/utils'
import { negotiationRowDataGetter } from 'view/shared/datagrid'
import { TypedCellRendererParams } from 'view/components/datagrid'
import { formatDuration } from './utils'
import { DateLike, getMinTime } from 'utils'
import { Translation, Locale } from 'locale'
import * as mixins from 'view/styles/mixins'
import { isNotNil } from 'utils/predicate'

export interface TimeRendererProps<T = NegotiationDetail> extends TypedCellRendererParams<T> {
  which: 'otw' | 'rfq'
}

const Container = styled.div`
  ${mixins.flexCenterContentCrossAxis};
  justify-content: flex-end;
  width: 100%;
  height: 100%;
  padding: 0 10px;
`

export class TimeRenderer extends React.Component<TimeRendererProps> {
  render() {
    const { node, which } = this.props
    const rowParams = { ...this.props }
    const rowData = negotiationRowDataGetter<NegotiationDetail>(rowParams, true)
    const allLeafChildren = node.allLeafChildren ?? []

    // Render nothing on a group row with no negotiation or an expanded group row with no/OCO negotiation
    // Timers on OCO summary row behaves like non-collection group rows (displays nothing on an expanded group)
    if (
      node.group
        ? node.expanded &&
          (!rowData ||
            rowData.negotiation.negotiationType === NegotiationType.ONE_CANCELS_OTHERS_SUMMARY ||
            rowData.negotiation.negotiationType === NegotiationType.AON_X_SUMMARY)
        : !rowData
    ) {
      return null
    }

    // If row data is null at this point, then the row must be a non-collection group row
    // Timers on OCO summary row behaves like non-collection group rows (displays min OTW of children when group is collapsed)
    const negotiationDetails: NegotiationDetail[] =
      rowData &&
      rowData.negotiation.negotiationType !== NegotiationType.ONE_CANCELS_OTHERS_SUMMARY &&
      rowData.negotiation.negotiationType !== NegotiationType.AON_X_SUMMARY
        ? [rowData]
        : allLeafChildren.map(leaf => leaf.data).filter(isNotNil)

    let endTime: DateLike | null = null

    if (which === 'rfq') {
      endTime = this.getRfqEndTime(negotiationDetails)
    } else if (which === 'otw') {
      if (negotiationDetails.some(negotiationDetail => negotiationDetail?.negotiation?.status === NegotiationStatus.QUOTED)) {
        endTime = this.getOtwEndTime(negotiationDetails)
      } else if (
        rowData &&
        rowData.negotiation?.negotiationType !== NegotiationType.CONTINGENT_ITEM &&
        rowData.negotiation?.negotiationType !== NegotiationType.ONE_CANCELS_OTHERS_SUMMARY &&
        negotiationDetails[0] &&
        isQuotable(negotiationDetails[0].negotiation.status)
      ) {
        return this.renderOTWSelection(negotiationDetails[0])
      }
    }

    return endTime ? <CountdownTimer expiryTime={moment(endTime)} /> : <Container className="validation-cell">-</Container>
  }

  private getRfqEndTime = (negotiationDetails: NegotiationDetail[]) => {
    const rfqEndTimes = negotiationDetails.map(negotiationDetail => negotiationDetail?.negotiation?.rfqEndTime)
    return isTerminalStatus(negotiationDetails[0]?.negotiation?.status) ? null : getMinTime(rfqEndTimes)
  }

  private getOtwEndTime = (negotiationDetails: NegotiationDetail[]) => {
    const otwEndTimes = negotiationDetails
      .filter(negotiationDetail => negotiationDetail?.negotiation?.status === NegotiationStatus.QUOTED)
      .map(negotiationDetail => negotiationDetail?.negotiation?.otwEndTime || negotiationDetail?.negotiation?.rfqEndTime)
    return otwEndTimes.length > 0 ? getMinTime(otwEndTimes) : null
  }

  private renderOTWSelection = (negotiationDetail: NegotiationDetail) => {
    const isValidOTWDuration = getIsValidOTWDuration(negotiationDetail)
    const containerClassname = classNames('validation-cell', { 'severity-error': !isValidOTWDuration })
    return (
      <Tooltip tooltip={isValidOTWDuration ? '' : Locale.translate(Translation.SC_INVALID_OTW)}>
        <Container className={containerClassname}>{negotiationDetail.ownOrder ? '-' : formatDuration(this.props.getValue?.())}</Container>
      </Tooltip>
    )
  }

  refresh = () => {
    this.forceUpdate()
    return true
  }
}
